import { Injectable, inject } from '@angular/core';
import { Observable, of, ReplaySubject } from 'rxjs';
import { IListadoCategoria, IQueryParam } from 'modelos/src';
import { ListadoCategoriasService } from '../../../auxiliares/servicios/http/listado-categorias.service';

@Injectable({
  providedIn: 'root',
})
export class ListadoCategoriasSelectorCacheService {
  private listadoCategoriasService = inject(ListadoCategoriasService);

  private cache = new Map<string, IListadoCategoria[]>();
  private inFlight = new Map<string, ReplaySubject<IListadoCategoria[]>>();
  private cacheTimestamp = new Map<string, number>();
  private readonly CACHE_DURATION = 5 * 60 * 1000;

  private getCacheKey(
    idCliente?: string,
    categoria?: string,
    categorias?: string[],
  ): string {
    const categoriasKey = categorias?.length
      ? categorias.sort().join(',')
      : (categoria ?? '__all__');
    return `${idCliente ?? '__all__'}:${categoriasKey}`;
  }

  getListadoCategorias(
    idCliente?: string,
    categoria?: string,
    categorias?: string[],
  ): Observable<IListadoCategoria[]> {
    const cacheKey = this.getCacheKey(idCliente, categoria, categorias);
    const now = Date.now();
    const timestamp = this.cacheTimestamp.get(cacheKey);

    // Caché válido → devolverlo de inmediato
    if (
      this.cache.has(cacheKey) &&
      timestamp !== undefined &&
      now - timestamp < this.CACHE_DURATION
    ) {
      return of(this.cache.get(cacheKey)!);
    }

    // Petición en vuelo → compartir el subject
    if (this.inFlight.has(cacheKey)) {
      return this.inFlight.get(cacheKey)!.asObservable();
    }

    // Nueva petición
    const subject = new ReplaySubject<IListadoCategoria[]>(1);
    this.inFlight.set(cacheKey, subject);

    // Obtener categorías del cliente O categorías globales, filtrando por categoría si se proporciona
    const filterObj: Record<string, unknown> = {};
    const condiciones: Record<string, unknown>[] = [];

    // Filtro de cliente/global
    if (idCliente) {
      condiciones.push({ global: true });
      condiciones.push({ idCliente: idCliente });
      filterObj['$or'] = condiciones;
    }

    // Filtro de categoría (múltiples o una sola)
    if (categorias && categorias.length > 0) {
      filterObj['categoria'] = { $in: categorias };
    } else if (categoria) {
      filterObj['categoria'] = categoria;
    }

    const query: IQueryParam = {
      select: 'nombre idCliente global',
      sort: 'nombre',
      unlimited: true,
      ...(Object.keys(filterObj).length
        ? { filter: JSON.stringify(filterObj) }
        : {}),
    };

    this.listadoCategoriasService
      .getFiltered(query)
      .then((data) => {
        const categorias = (data.datos ?? []).sort((a, b) =>
          (a.nombre ?? '').localeCompare(b.nombre ?? ''),
        );

        this.cache.set(cacheKey, categorias);
        this.cacheTimestamp.set(cacheKey, Date.now());
        this.inFlight.delete(cacheKey);

        subject.next(categorias);
        subject.complete();
      })
      .catch((err) => {
        this.inFlight.delete(cacheKey);
        subject.error(err);
      });

    return subject.asObservable();
  }

  invalidate(
    idCliente?: string,
    categoria?: string,
    categorias?: string[],
  ): void {
    const key = this.getCacheKey(idCliente, categoria, categorias);
    this.cache.delete(key);
    this.cacheTimestamp.delete(key);
    this.inFlight.delete(key);
  }
}
